/**
 * The daily 08:00 America/Chicago boundary that invalidates this screen's cache.
 *
 * ## Why 08:00 Central
 *
 * `RW-Marketing-Service` schedules every ingest backwards from an **08:00
 * America/Chicago deadline** — the heal sweep runs 07:30 daily, and Thursday's
 * FULL chain runs 01:00 through 06:00. So 08:00 Central is the moment the
 * previous day's cached figures stop being the newest answer.
 *
 * ## Everything here is computed in Central, never in local time
 *
 * `getHours()`, `getDate()` and friends read the VIEWER's zone. A viewer in
 * Delhi or London would get a boundary hours away from the one the service
 * actually works to, and the bug would be invisible to anyone testing in a
 * single timezone. Every function below goes through `Intl.DateTimeFormat` with
 * `timeZone: CENTRAL_TIME_ZONE`, which is a NAMED zone — so the CST/CDT
 * daylight shift is the platform's problem, exactly as `utils/centralTimestamp.ts` already
 * relies on for rendering, and as the service's own `marketing.scheduler.zone`
 * relies on for scheduling.
 *
 * ## The clock is the server's, not the browser's
 *
 * Callers pass `now` in, and the caller is expected to pass `serverNow()` from
 * `lib/serverClock.ts`. The boundary rule has to hold on a machine with a wrong
 * clock; see that module for why the server's `Date` header is the anchor.
 */
import { CENTRAL_TIME_ZONE } from 'utils/centralTimestamp';

/**
 * The hour, in Central wall-clock terms, at which a cache entry stops being
 * current. Named rather than inlined because it is a business fact — it mirrors
 * the service's 08:00 deadline — not an arbitrary tuning number.
 */
export const MARKETING_REFRESH_HOUR_CENTRAL = 8;

/** Milliseconds in a minute / hour, so the arithmetic below reads as arithmetic. */
const MS_PER_SECOND = 1000;

/**
 * Formatter used to read an instant's Central wall clock.
 *
 * Built once at module load: constructing an `Intl` formatter is comparatively
 * expensive, and this is consulted on every cache read. `h23` so midnight is 00
 * rather than 24 — `hourCycle: 'h12'` would make the hour comparison below wrong
 * twice a day, which is precisely the kind of error that only shows up at 12:xx.
 */
const CENTRAL_PARTS_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: CENTRAL_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** One instant, expressed as a Central wall clock. */
interface CentralWallClock {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

/**
 * Read an instant's Central wall-clock parts.
 *
 * `formatToParts` rather than parsing a formatted string: the separators and
 * ordering `Intl` emits vary between ICU versions, and a regex over the rendered
 * form is the kind of thing that passes on one machine and fails on another
 * (`utils/centralTimestamp.ts` documents hitting exactly that with narrow no-break
 * spaces).
 */
function centralWallClock(instantMs: number): CentralWallClock {
  const parts = CENTRAL_PARTS_FORMAT.formatToParts(new Date(instantMs));
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((candidate) => candidate.type === type);
    return part ? Number(part.value) : 0;
  };

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
}

/**
 * The zone's offset from UTC at `instantMs`, in milliseconds (negative for
 * Central).
 *
 * Derived by reading the same instant as a Central wall clock and then asking
 * what instant those parts would be if they were UTC. The difference IS the
 * offset. Computed per instant rather than assumed, which is what makes the two
 * daylight-saving days come out right instead of being an hour wrong for one of
 * them.
 *
 * `instantMs` is floored to the second because `Intl` does not report
 * milliseconds — comparing against the unfloored value would fold the
 * sub-second remainder into the offset.
 */
function centralOffsetMs(instantMs: number): number {
  const wall = centralWallClock(instantMs);
  const asIfUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  );
  return asIfUtc - Math.floor(instantMs / MS_PER_SECOND) * MS_PER_SECOND;
}

/**
 * The instant at which a given Central wall-clock date reads
 * `MARKETING_REFRESH_HOUR_CENTRAL:00:00`.
 *
 * **Two passes, and the second one is not redundant.** The offset can only be
 * looked up for an INSTANT, but the instant is what we are solving for — so the
 * first pass guesses using the offset at the wall clock read as UTC, and the
 * second re-evaluates at the candidate answer. On a daylight-saving day the two
 * can disagree by an hour, and the second reading is the one taken at (or beside)
 * the boundary itself. 08:00 sits four hours clear of the 02:00 transition, so
 * this converges immediately in practice; the second pass is what makes that a
 * property of the code rather than a lucky choice of hour.
 */
function centralDateAtRefreshHour(year: number, month: number, day: number): number {
  const wallAsUtc = Date.UTC(year, month - 1, day, MARKETING_REFRESH_HOUR_CENTRAL, 0, 0);

  const firstOffset = centralOffsetMs(wallAsUtc);
  const candidate = wallAsUtc - firstOffset;
  const secondOffset = centralOffsetMs(candidate);

  return secondOffset === firstOffset ? candidate : wallAsUtc - secondOffset;
}

/** Shift a Central calendar date by whole days, without touching a local-time API. */
function shiftCentralDate(
  wall: CentralWallClock,
  days: number,
): { year: number; month: number; day: number } {
  // `Date.UTC` normalises an out-of-range day, so month and year ends need no
  // special case. This is calendar arithmetic on the Central DATE only — no
  // instant is implied, so the zone offset is irrelevant here.
  const shifted = new Date(Date.UTC(wall.year, wall.month - 1, wall.day + days));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

/**
 * The most recent 08:00 Central, as an epoch.
 *
 * Before 08:00 Central that is yesterday's; from 08:00 onwards it is today's.
 * Anything written before the returned instant is describing a load older than
 * the newest one the service has performed.
 *
 * @param nowMs the current instant — pass `serverNow()`
 */
export function latestBoundary(nowMs: number): number {
  const wall = centralWallClock(nowMs);

  const date = wall.hour < MARKETING_REFRESH_HOUR_CENTRAL
    ? shiftCentralDate(wall, -1)
    : { year: wall.year, month: wall.month, day: wall.day };

  return centralDateAtRefreshHour(date.year, date.month, date.day);
}

/**
 * The next 08:00 Central, as an epoch.
 *
 * Always strictly in the future: called exactly AT the boundary it returns
 * tomorrow's rather than the same instant, so a timer armed from it cannot fire
 * in a zero-delay loop.
 */
export function nextBoundary(nowMs: number): number {
  const last = latestBoundary(nowMs);
  const date = shiftCentralDate(centralWallClock(last), 1);
  return centralDateAtRefreshHour(date.year, date.month, date.day);
}

/**
 * Whether an entry written at `cachedAtMs` predates the last boundary — i.e.
 * whether it must be checked against the server before being shown again.
 *
 * This is the whole invalidation rule, and it is evaluated on every read rather
 * than trusted to a timer, so no code path can serve pre-boundary data by
 * forgetting to schedule something.
 */
export function isPastBoundary(cachedAtMs: number, nowMs: number): boolean {
  return cachedAtMs < latestBoundary(nowMs);
}

/**
 * Milliseconds until the next 08:00 Central — for arming a single timer so an
 * open, idle tab revalidates at the boundary instead of showing yesterday's
 * figures until someone touches a filter.
 *
 * Never returns 0 or a negative number, so a caller cannot arm a same-tick
 * timeout. On the two daylight-saving days the real gap is 23 or 25 hours, which
 * falls out of the wall-clock conversion rather than needing a special case.
 */
export function msUntilNextBoundary(nowMs: number): number {
  return Math.max(1, nextBoundary(nowMs) - nowMs);
}
